import { useState } from 'react';

import { useMediaQuery } from 'react-responsive';

import { ChevronDown, Filter } from 'lucide-react';
import { useQuotation } from '../../../../../../hooks/sale/useQuotation';
import POSProductFilterContent from '../../../../pos/filter/POSProductFilterContent';
import POSPriceFilterModal from '../../../../pos/filter/POSPriceFilterModal';
import useProductCatalog from '../../../../../../hooks/productpanel/useProductCatalog';
import POSCategoryFilterModal from '../../../../pos/filter/POSCategoryFilterModal';
import POSSubCategoryFilterModal from '../../../../pos/filter/POSSubCategoryFilterModal';
import POSBrandFilterModal from '../../../../pos/filter/POSBrandFilterModal';

const QuotationProductFilters = () => {
  // =====================================================
  // 🔹 State
  // =====================================================
  const [open, setOpen] = useState(false);

  const [showPriceModal, setShowPriceModal] = useState(false);

  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);

  const [showBrandModal, setShowBrandModal] = useState(false);

  const [priceMax, setPriceMax] = useState(100);

  const [selectedPromos, setSelectedPromos] = useState<number[]>([]);

  // =====================================================
  // 🔹 Responsive
  // =====================================================
  const isMobile = useMediaQuery({
    maxWidth: 639,
  });

  // =====================================================
  // 🔹 Quotation State
  // =====================================================
  const { activeBranchId } = useQuotation();

  const branchId = activeBranchId;

  const isQuotationReady = !!branchId;

  const {
    fetchFilteredProducts,

    categories,
    subCategories,
    brands,

    selectedCategoryId,
    selectedSubCategoryId,
    selectedBrandId,

    setSelectedCategory,
    setSelectedSubCategory,
    setSelectedBrand,

    pageSize,
  } = useProductCatalog(branchId);

  const selectedCategoryName = categories.find(
    (x) => x.id === selectedCategoryId
  )?.name;

  const selectedSubCategoryName = subCategories.find(
    (x) => x.id === selectedSubCategoryId
  )?.name;

  const selectedBrandName = brands.find((x) => x.id === selectedBrandId)?.name;

  // =====================================================
  // 🔹 Apply Filters
  // =====================================================
  const handleApplyFilters = () => {
    if (!branchId) {
      return;
    }

    fetchFilteredProducts(
      {
        branchId,

        categoryId: selectedCategoryId ?? undefined,

        subCategoryId: selectedSubCategoryId ?? undefined,

        brandId: selectedBrandId ?? undefined,

        price: priceMax,

        promotionIds: selectedPromos,
      },
      0,
      pageSize
    );

    setOpen(false);
  };

  // =====================================================
  // 🔹 Clear Filters
  // =====================================================
  const handleClearFilters = () => {
    if (!branchId) {
      return;
    }

    setPriceMax(100);

    setSelectedPromos([]);

    fetchFilteredProducts(
      {
        branchId,
      },
      0,
      pageSize
    );

    setOpen(false);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div className="relative">
      {/* ===================================================== */}
      {/* 🔹 Trigger */}
      {/* ===================================================== */}
      <button
        type="button"
        disabled={!isQuotationReady}
        onClick={() => setOpen(!open)}
        className={`
        inline-flex
        h-[44px]
        items-center
        gap-2

        rounded-xl
        border

        px-4

        text-sm
        font-medium

        shadow-1

        transition-all
        duration-200

        ${
          isQuotationReady
            ? `
              border-slate-300
              bg-white
              text-slate-700

              hover:border-primary/50
              hover:text-primary
              hover:shadow-2

              focus:border-primary
              focus:ring-4
              focus:ring-primary/15
            `
            : `
              cursor-not-allowed

              border-slate-200
              bg-slate-100

              text-slate-400
            `
        }

        ${
          open
            ? `
              border-primary
              text-primary

              shadow-2
              ring-4

              ring-primary/15
            `
            : ''
        }

        dark:border-form-strokedark
        dark:bg-boxdark-2
        dark:text-white

        dark:hover:border-primary/50
        dark:hover:text-primary

        dark:focus:border-primary
        dark:focus:ring-4
        dark:focus:ring-primary/20
      `}
      >
        <Filter className="h-4 w-4" />

        <span>Filtrar</span>

        <ChevronDown
          className={`
      h-4
      w-4

      transition-transform
      duration-200

      ${open ? 'rotate-180' : ''}
    `}
        />
      </button>

      {/* ===================================================== */}
      {/* 🔹 Dropdown */}
      {/* ===================================================== */}
      {open && (
        <>
          {/* ===================================================== */}
          {/* 🔹 Desktop */}
          {/* ===================================================== */}
          {!isMobile && (
            <div
              className="
                absolute
                right-0
                top-[52px]
                z-50

                w-[320px]

                rounded-xl

                border border-stroke

                bg-white

                p-4

                shadow-xl

                dark:border-strokedark
                dark:bg-boxdark
              "
            >
              <POSProductFilterContent
                priceMax={priceMax}
                selectedCategoryName={selectedCategoryName}
                selectedSubCategoryName={selectedSubCategoryName}
                selectedBrandName={selectedBrandName}
                setShowPriceModal={setShowPriceModal}
                setShowCategoryModal={setShowCategoryModal}
                setShowSubCategoryModal={setShowSubCategoryModal}
                setShowBrandModal={setShowBrandModal}
                handleApplyFilters={handleApplyFilters}
                handleClearFilters={handleClearFilters}
                isSaleInitialized={isQuotationReady}
              />
            </div>
          )}

          {/* ===================================================== */}
          {/* 🔹 Mobile */}
          {/* ===================================================== */}
          {isMobile && (
            <div
              className="
                fixed
                inset-x-0
                bottom-0
                z-[999]

                rounded-t-3xl

                border-t border-stroke

                bg-white

                p-5

                shadow-2xl

                dark:border-strokedark
                dark:bg-boxdark
              "
            >
              <POSProductFilterContent
                priceMax={priceMax}
                selectedCategoryName={selectedCategoryName}
                selectedSubCategoryName={selectedSubCategoryName}
                selectedBrandName={selectedBrandName}
                setShowPriceModal={setShowPriceModal}
                setShowCategoryModal={setShowCategoryModal}
                setShowSubCategoryModal={setShowSubCategoryModal}
                setShowBrandModal={setShowBrandModal}
                handleApplyFilters={handleApplyFilters}
                handleClearFilters={handleClearFilters}
                isSaleInitialized={isQuotationReady}
              />
            </div>
          )}
        </>
      )}

      {/* ===================================================== */}
      {/* 🔹 Modal */}
      {/* ===================================================== */}
      <POSPriceFilterModal
        isOpen={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        onConfirm={(value) => setPriceMax(value)}
        initialValue={priceMax}
      />

      <POSCategoryFilterModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        branchId={branchId ?? 0}
        selectedCategoryId={selectedCategoryId}
        onSelect={(categoryId) => {
          setSelectedCategory(categoryId);
          setSelectedSubCategory(null);
        }}
      />

      <POSSubCategoryFilterModal
        isOpen={showSubCategoryModal}
        onClose={() => setShowSubCategoryModal(false)}
        branchId={branchId ?? 0}
        categoryId={selectedCategoryId}
        selectedSubCategoryId={selectedSubCategoryId}
        onSelect={setSelectedSubCategory}
      />

      <POSBrandFilterModal
        isOpen={showBrandModal}
        onClose={() => setShowBrandModal(false)}
        branchId={branchId ?? 0}
        selectedBrandId={selectedBrandId}
        onSelect={setSelectedBrand}
      />
    </div>
  );
};

export default QuotationProductFilters;
